import {
  Box,
  Flex,
  Heading,
  Input,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { message, Upload, UploadFile } from 'antd';
import React from 'react';
import { Control, Controller } from 'react-hook-form';

import ButtonCustom from '@/app/_components/button/ButtonCustom';
import { ReactComponent as UploadImage } from '@/app/_icons/upload_image.svg';
import { SELL_YOUR_CAR_MAX_PHOTOS } from '@/constants/sell-your-car/sell-your-car.constants';

import Styles from '../SellYourCarForm.module.scss';

interface PhotoUploadProps {
  control: Control<any>;
  isDragging: boolean;
  setIsDragging: React.Dispatch<React.SetStateAction<boolean>>;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({
  control,
  isDragging,
  setIsDragging,
}) => {
  const toast = useToast();

  return (
    <Stack spacing={6}>
      <Text textAlign={'center'} as="b">
        *The more we know about the vehicle the more accurately we can quote you
      </Text>
      <Controller
        name="photos"
        control={control}
        render={({ field: { onChange, value } }) => {
          const photoCount = value ? value.length : 0;

          const handleClick = () => {
            if (photoCount >= SELL_YOUR_CAR_MAX_PHOTOS) {
              toast({
                title: 'Maximum photos reached',
                description: `You've already uploaded the maximum of ${SELL_YOUR_CAR_MAX_PHOTOS} photos.`,
                status: 'warning',
                duration: 3000,
                isClosable: true,
              });
              return;
            }

            const uploadElement = document.querySelector(
              '.ant-upload-select input[type="file"]',
            );

            if (uploadElement) {
              (uploadElement as HTMLElement).click();
            }
          };

          const handleDragEnter = (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(true);
          };

          const handleDragLeave = (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);
          };

          const handleDragOver = (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isDragging) {
              setIsDragging(true);
            }
          };

          const handleDrop = (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);

            if (photoCount >= SELL_YOUR_CAR_MAX_PHOTOS) {
              toast({
                title: 'Maximum photos reached',
                description: `You've already uploaded the maximum of ${SELL_YOUR_CAR_MAX_PHOTOS} photos.`,
                status: 'warning',
                duration: 3000,
                isClosable: true,
              });
              return;
            }

            const files = Array.from(e.dataTransfer.files);
            const imageFiles = files.filter((file) =>
              file.type.startsWith('image/'),
            );

            if (imageFiles.length > 0) {
              const currentFileList = value || [];

              const newFiles = imageFiles.map((file) => ({
                uid:
                  typeof window !== 'undefined'
                    ? crypto.randomUUID()
                    : 'fallback-id',
                name: file.name,
                status: 'done',
                url: URL.createObjectURL(file),
                originFileObj: file,
                type: file.type,
                size: file.size,
                lastModified: file.lastModified,
              }));

              const combinedFiles = [...currentFileList, ...newFiles].slice(
                0,
                SELL_YOUR_CAR_MAX_PHOTOS,
              );

              onChange(combinedFiles);

              if (
                combinedFiles.length >= SELL_YOUR_CAR_MAX_PHOTOS &&
                newFiles.length >
                  SELL_YOUR_CAR_MAX_PHOTOS - currentFileList.length
              ) {
                message.warning(
                  `Maximum ${SELL_YOUR_CAR_MAX_PHOTOS} images allowed`,
                );
                toast({
                  title: 'Too many images',
                  description: `Maximum ${SELL_YOUR_CAR_MAX_PHOTOS} images allowed`,
                  status: 'warning',
                  duration: 3000,
                  isClosable: true,
                });
              }
            } else if (files.length > 0) {
              message.error('You can only upload image files!');
              toast({
                title: 'Invalid file type',
                description: 'Only image files are allowed',
                status: 'error',
                duration: 3000,
                isClosable: true,
              });
            }
          };

          const handleRemove = (file: UploadFile) => {
            onChange(value.filter((item: UploadFile) => item.uid !== file.uid));
            return true;
          };

          return (
            <Flex direction="column" align="center">
              {/* Simple photo count indicator */}
              <Text mb={2} fontSize="sm" textAlign="right" width="100%">
                {photoCount > 0 ? (
                  <span>
                    {photoCount}/{SELL_YOUR_CAR_MAX_PHOTOS} photos
                    {photoCount >= SELL_YOUR_CAR_MAX_PHOTOS && (
                      <span style={{ color: 'red' }}> (Maximum reached)</span>
                    )}
                  </span>
                ) : null}
              </Text>

              <Box
                borderWidth={1}
                borderStyle="dashed"
                borderRadius="md"
                p={6}
                textAlign="center"
                className={`${Styles.uploadBox} ${isDragging ? Styles.dragging : ''} ${photoCount >= SELL_YOUR_CAR_MAX_PHOTOS ? Styles.disabled : ''}`}
                onClick={handleClick}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                opacity={photoCount >= SELL_YOUR_CAR_MAX_PHOTOS ? 0.6 : 1}
              >
                <Flex mb={3} justify={'center'} align={'center'}>
                  <UploadImage />
                </Flex>
                <Flex gap={8} justify="center" className={Styles.uploadButtons}>
                  <Box className={`${Styles.formActions} ${Styles.dragBtn}`}>
                    <ButtonCustom
                      moduleClass={Styles.ctaBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClick();
                      }}
                      disabled={photoCount >= SELL_YOUR_CAR_MAX_PHOTOS}
                    >
                      Drag
                    </ButtonCustom>
                  </Box>

                  <Box className={Styles.formActions}>
                    <ButtonCustom
                      moduleClass={Styles.ctaBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClick();
                      }}
                      disabled={photoCount >= SELL_YOUR_CAR_MAX_PHOTOS}
                    >
                      Upload
                    </ButtonCustom>
                  </Box>
                </Flex>
                <Upload
                  className={Styles.uploadContainer}
                  fileList={[]}
                  beforeUpload={(file) => {
                    if (photoCount >= SELL_YOUR_CAR_MAX_PHOTOS) {
                      toast({
                        title: `Maximum ${SELL_YOUR_CAR_MAX_PHOTOS} photos reached`,
                        status: 'warning',
                        duration: 2000,
                        isClosable: true,
                      });
                      return Upload.LIST_IGNORE;
                    }

                    const isImage = file.type.startsWith('image/');

                    if (!isImage) {
                      message.error('You can only upload image files!');
                      toast({
                        title: 'Invalid file type',
                        description: 'Only image files are allowed',
                        status: 'error',
                        duration: 3000,
                        isClosable: true,
                      });
                      return Upload.LIST_IGNORE;
                    }

                    const isLt5M = file.size / 1024 / 1024 < 5;

                    if (!isLt5M) {
                      message.error('Image must be smaller than 5MB!');
                      toast({
                        title: 'File too large',
                        description: 'Image must be smaller than 5MB',
                        status: 'error',
                        duration: 3000,
                        isClosable: true,
                      });
                      return Upload.LIST_IGNORE;
                    }

                    return false;
                  }}
                  onChange={({ fileList }) => {
                    if (fileList.length > 0) {
                      const currentFileList = value || [];

                      if (currentFileList.length >= SELL_YOUR_CAR_MAX_PHOTOS) {
                        toast({
                          title: 'Maximum photos reached',
                          description: `You've already uploaded the maximum of ${SELL_YOUR_CAR_MAX_PHOTOS} photos.`,
                          status: 'warning',
                          duration: 3000,
                          isClosable: true,
                        });
                        return;
                      }

                      const newFiles = fileList.map((file) => ({
                        ...file,
                        url: URL.createObjectURL(file.originFileObj as File),
                      }));

                      const combinedFiles = [
                        ...currentFileList,
                        ...newFiles,
                      ].slice(0, SELL_YOUR_CAR_MAX_PHOTOS);

                      if (combinedFiles.length > SELL_YOUR_CAR_MAX_PHOTOS) {
                        message.warning(
                          `Maximum ${SELL_YOUR_CAR_MAX_PHOTOS} images allowed`,
                        );
                        toast({
                          title: 'Too many images',
                          description: `Maximum ${SELL_YOUR_CAR_MAX_PHOTOS} images allowed`,
                          status: 'warning',
                          duration: 3000,
                          isClosable: true,
                        });
                      }

                      onChange(combinedFiles);
                    }
                  }}
                  multiple
                  accept="image/*"
                  maxCount={SELL_YOUR_CAR_MAX_PHOTOS}
                  showUploadList={false}
                  disabled={photoCount >= SELL_YOUR_CAR_MAX_PHOTOS}
                >
                  <div className={Styles.uploadNote}>
                    {photoCount >= SELL_YOUR_CAR_MAX_PHOTOS ? (
                      <p className={Styles.maxReached}>
                        Maximum {SELL_YOUR_CAR_MAX_PHOTOS} photos reached
                      </p>
                    ) : (
                      <>
                        <p>Click or drag image files to upload</p>
                        <p className={Styles.optional}>
                          *optional (max {SELL_YOUR_CAR_MAX_PHOTOS} images)
                        </p>
                      </>
                    )}
                  </div>
                </Upload>
              </Box>

              {value && value.length > 0 && (
                <Box className={Styles.imagePreviewContainer} mt={4}>
                  <Upload
                    listType="picture-card"
                    fileList={value}
                    onChange={() => {}}
                    onDrop={() => {}}
                    onRemove={handleRemove}
                    showUploadList={{
                      showPreviewIcon: true,
                      showRemoveIcon: true,
                      showDownloadIcon: false,
                    }}
                    onPreview={(file) => {
                      if (file.url) {
                        window.open(file.url);
                      }
                    }}
                  />
                </Box>
              )}

              <Heading
                as="h3"
                size="lg"
                mb={8}
                textAlign="center"
                className={Styles.stepHeading}
              >
                Is there anything else we should know about the vehicle?
              </Heading>
              <Controller
                name="message"
                control={control}
                rules={{
                  minLength: 3,
                }}
                render={({ field }) => (
                  <Box className={Styles.floatingForm}>
                    <Input
                      {...field}
                      placeholder="Type here"
                      type="text"
                      className={Styles.formInput}
                    />
                    <Text textAlign={'left'} className={Styles.optional}>
                      *optional
                    </Text>
                  </Box>
                )}
              />
            </Flex>
          );
        }}
      />
    </Stack>
  );
};

export default PhotoUpload;
